import formFieldWidget from "./formFieldWidget.js";

/**
 * Text input field and also base for all fields that use a single input.
 *
 * _Options:_
 * spec.append				Text to display before text field
 * spec.prepend				Text to display before after field
 * spec.placeHolder			Placeholder text to display as example in
 *							input when no value set by user.
 * spec.klass				Value for class attribute of the input field
 * spec.id					Input field id attribute. Uses spec.attribute as default
 * spec.name				Input field name attribute. Uses spec.attribute as default
 * spec.type				Input field type attribute (text as default)
 * spec.validateWhileTyping If true value will be validated on each keystroke
 * spec.disabled			If true, make the input field read-only
 * spec.onKeyUp             Function to be executed on keyup event.
 *
 * See formField for more options
 *
 * _Note:_ Override ´my.renderInputFieldOn´. Should render *and return* input field.
 */
function inputFieldWidget(spec, my) {
	spec ||= {};
	my ||= {};

	const that = formFieldWidget(spec, my);

	spec.defaultValue ||= "";
	my.autocomplete = spec.autocomplete !== false;
	my.takeFocus = spec.takeFocus;
	my.keyup = spec.onKeyUp || (() => {});

	my.field = null;

	// Read value from field
	my.readValue = function () {
		return my.field.asJQuery().val();
	};

	// Set value to value from field
	my.updateValue = function (preventValidation) {
		that.setValue(my.readValue(), preventValidation);
	};

	my.renderFieldOn = function (html) {
		const div = spec.prepend || spec.append ? html.div() : html;

		if (spec.prepend) {
			div.addClass("input-prepend");
			div.render(html.span({ klass: "add-on" }, spec.prepend));
		}

		div.render((html) => {
			my.field = my.renderInputFieldOn(html);
		});

		if (spec.validateWhileTyping) {
			my.field.keyup(() => {
				that.validate(my.readValue());
			});
		}

		// change on blur
		my.registerChangeOnBlur();

		// Nico: I tested the form with the following commented
		// out, and AFAICT everything works fine.  Acting on the
		// change events prevents the form to be submitted on ENTER
		// (see issue
		// https://foretagsplatsen.atlassian.net/secure/RapidBoard.jspa?rapidView=5&view=detail&selectedIssue=FTGP-1161)

		// my.field.change(my.updateValue);

		if (spec.append) {
			div.addClass("input-append");
			div.render(html.span({ klass: "add-on" }, spec.append));
		}
	};

	my.didAttach = function () {
		if (!my.takeFocus) {
			return;
		}
		that.asJQuery().find("input").focus();
	};

	my.registerChangeOnBlur = function () {
		my.field.blur(() => {
			my.updateValue(!that.getValidateOnBlur());
		});
	};

	my.renderInputFieldOn = function (html) {
		const options = {
			...my.attributes,
			klass: spec.klass || "form-control",
			type: spec.type || "text",
			name: spec.name || spec.attribute,
			value: that.getValue() || spec.defaultValue,
			autocomplete: my.autocomplete ? "on" : "off",
			placeHolder: spec.placeHolder || " ",
			keyup: my.keyup,
			"data-test": "input-field",
		};

		const id = spec.id || spec.attribute;

		if (id) {
			options.id = id;
		}

		const input = html.input(options);

		input.asJQuery().prop("disabled", !that.enabled());

		if (that.mutator && spec.type === "button") {
			input.click(() => that.mutator());
		}

		input.on("input", () => {
			my.updateValue(true);
			that.trigger("input");
		});

		return input;
	};

	return that;
}

export default inputFieldWidget;
